import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Space, Input, Select, DatePicker, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { LeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import StandardTable from '../../../components/common/StandardTable';
import type { VwMatGapRequestItemOutstanding, MatGapIssueRequestStatus } from '../../../types';
import { useGetVwMatGapRequestItemOutstanding, useGetMatWarehouses } from '../../../hooks/genericHooks';
import type { MatWarehouses } from '../../../types';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_OPTIONS: MatGapIssueRequestStatus[] = [
  'pending', 'approved', 'partially_issued', 'issued', 'rejected', 'cancelled'
];

export default function MatGapIssueRequestsItemList() {
  const navigate = useNavigate();

  const { data: src, isLoading, refetch } = useGetVwMatGapRequestItemOutstanding() as any;
  const data: VwMatGapRequestItemOutstanding[] = useMemo(() => (Array.isArray(src) ? src : (src?.data ?? [])), [src]);

  // Warehouses map for names
  const { data: whRaw = [] } = useGetMatWarehouses() as any;
  const warehouses: MatWarehouses[] = useMemo(() => (Array.isArray(whRaw) ? whRaw : (whRaw?.data ?? [])), [whRaw]);
  const whMap = useMemo(() => {
    const m = new Map<number, string>();
    (warehouses || []).forEach(w => { if (w?.id != null) m.set(Number(w.id), String(w.name)); });
    return m;
  }, [warehouses]);

  // Filters
  const [refNo, setRefNo] = useState<string>('');
  const [requester, setRequester] = useState<string>('');
  const [requesterName, setRequesterName] = useState<string>('');
  const [status, setStatus] = useState<MatGapIssueRequestStatus | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [matId, setMatId] = useState<string>('');
  const [extGroup, setExtGroup] = useState<string>('');
  const [warehouse, setWarehouse] = useState<string>('');
  const [descSearch, setDescSearch] = useState<string>('');
  const [debouncedDesc, setDebouncedDesc] = useState<string>('');

  const norm = (s: any) => (s == null ? '' : String(s)).trim().toLowerCase();

  // Debounce description search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedDesc(descSearch), 300);
    return () => clearTimeout(t);
  }, [descSearch]);

  // Base (date+status) for options to cascade
  const baseForOptions = useMemo(() => {
    const [start, end] = dateRange || [null, null];
    return (data || []).filter((r) => {
      if (status && r.status !== status) return false;
      if (start && r.req_date && dayjs(r.req_date).isBefore(start, 'day')) return false;
      if (end && r.req_date && dayjs(r.req_date).isAfter(end, 'day')) return false;
      return true;
    });
  }, [data, status, dateRange]);

  // Cascading option lists
  const refNoOptions = useMemo(() => {
    const s = new Set<string>();
    baseForOptions
      .filter(r => !requester || String(r.requester) === requester)
      .filter(r => !requesterName || norm(r.requester_name).includes(norm(requesterName)))
      .filter(r => !matId || String(r.mat_id) === matId)
      .filter(r => !extGroup || norm(r.ext_group).includes(norm(extGroup)))
      .filter(r => !warehouse || String(r.requested_wh_id ?? '') === warehouse)
      .forEach(r => { if (r?.ref_no) s.add(String(r.ref_no)); });
    return Array.from(s).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [baseForOptions, requester, requesterName, matId, extGroup, warehouse]);

  const requesterOptions = useMemo(() => {
    const s = new Set<string>();
    baseForOptions
      .filter(r => !refNo || String(r.ref_no) === refNo)
      .filter(r => !requesterName || norm(r.requester_name).includes(norm(requesterName)))
      .filter(r => !matId || String(r.mat_id) === matId)
      .filter(r => !extGroup || norm(r.ext_group).includes(norm(extGroup)))
      .filter(r => !warehouse || String(r.requested_wh_id ?? '') === warehouse)
      .forEach(r => { if (r?.requester) s.add(String(r.requester)); });
    return Array.from(s).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [baseForOptions, refNo, requesterName, matId, extGroup, warehouse]);

  const requesterNameOptions = useMemo(() => {
    const s = new Set<string>();
    baseForOptions
      .filter(r => !refNo || String(r.ref_no) === refNo)
      .filter(r => !requester || String(r.requester) === requester)
      .filter(r => !matId || String(r.mat_id) === matId)
      .filter(r => !extGroup || norm(r.ext_group).includes(norm(extGroup)))
      .filter(r => !warehouse || String(r.requested_wh_id ?? '') === warehouse)
      .forEach(r => { if (r?.requester_name) s.add(String(r.requester_name)); });
    return Array.from(s).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [baseForOptions, refNo, requester, matId, extGroup, warehouse]);

  const matIdOptions = useMemo(() => {
    const s = new Set<string>();
    baseForOptions
      .filter(r => !refNo || String(r.ref_no) === refNo)
      .filter(r => !requester || String(r.requester) === requester)
      .filter(r => !requesterName || norm(r.requester_name).includes(norm(requesterName)))
      .filter(r => !extGroup || norm(r.ext_group).includes(norm(extGroup)))
      .filter(r => !warehouse || String(r.requested_wh_id ?? '') === warehouse)
      .forEach(r => { if (r?.mat_id != null) s.add(String(r.mat_id)); });
    return Array.from(s).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [baseForOptions, refNo, requester, requesterName, extGroup, warehouse]);

  const extGroupOptions = useMemo(() => {
    const s = new Set<string>();
    baseForOptions
      .filter(r => !refNo || String(r.ref_no) === refNo)
      .filter(r => !requester || String(r.requester) === requester)
      .filter(r => !requesterName || norm(r.requester_name).includes(norm(requesterName)))
      .filter(r => !matId || String(r.mat_id) === matId)
      .filter(r => !warehouse || String(r.requested_wh_id ?? '') === warehouse)
      .forEach(r => { if (r?.ext_group) s.add(String(r.ext_group)); });
    return Array.from(s).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [baseForOptions, refNo, requester, requesterName, matId, warehouse]);

  const warehouseOptions = useMemo(() => {
    const s = new Set<string>();
    baseForOptions
      .filter(r => !refNo || String(r.ref_no) === refNo)
      .filter(r => !requester || String(r.requester) === requester)
      .filter(r => !requesterName || norm(r.requester_name).includes(norm(requesterName)))
      .filter(r => !matId || String(r.mat_id) === matId)
      .filter(r => !extGroup || norm(r.ext_group).includes(norm(extGroup)))
      .forEach(r => { if (r?.requested_wh_id != null) s.add(String(r.requested_wh_id)); });
    return Array.from(s)
      .sort((a,b)=>a.localeCompare(b))
      .map(v => ({ value: v, label: whMap.get(Number(v)) || v }));
  }, [baseForOptions, refNo, requester, requesterName, matId, extGroup]);

  // Auto-clear invalid selections
  useEffect(() => { if (refNo && !refNoOptions.some(o => o.value === refNo)) setRefNo(''); }, [refNoOptions, refNo]);
  useEffect(() => { if (requester && !requesterOptions.some(o => o.value === requester)) setRequester(''); }, [requesterOptions, requester]);
  useEffect(() => { if (requesterName && !requesterNameOptions.some(o => o.value === requesterName)) setRequesterName(''); }, [requesterNameOptions, requesterName]);
  useEffect(() => { if (matId && !matIdOptions.some(o => o.value === matId)) setMatId(''); }, [matIdOptions, matId]);
  useEffect(() => { if (extGroup && !extGroupOptions.some(o => o.value === extGroup)) setExtGroup(''); }, [extGroupOptions, extGroup]);
  useEffect(() => { if (warehouse && !warehouseOptions.some(o => o.value === warehouse)) setWarehouse(''); }, [warehouseOptions, warehouse]);

  const filtered = useMemo(() => {
    const [start, end] = dateRange || [null, null];
    const descTokens = (debouncedDesc || '')
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    return (data || []).filter((r) => {
      if (refNo && String(r.ref_no) !== refNo) return false;
      if (requester && String(r.requester) !== requester) return false;
      if (requesterName && !norm(r.requester_name).includes(norm(requesterName))) return false;
      if (status && r.status !== status) return false;
      if (matId && String(r.mat_id) !== matId) return false;
      if (extGroup && !norm(r.ext_group).includes(norm(extGroup))) return false;
      if (warehouse && String(r.requested_wh_id ?? '') !== warehouse) return false;
      if (descTokens.length > 0) {
        const hay = norm(r.description);
        if (!descTokens.every(t => hay.includes(t))) return false;
      }
      if (start && r.req_date && dayjs(r.req_date).isBefore(start, 'day')) return false;
      if (end && r.req_date && dayjs(r.req_date).isAfter(end, 'day')) return false;
      return true;
    });
  }, [data, refNo, requester, requesterName, status, matId, extGroup, warehouse, dateRange, debouncedDesc]);

  const columns: ColumnsType<VwMatGapRequestItemOutstanding> = useMemo(() => [
    { title: 'Ref No', dataIndex: 'ref_no', key: 'ref_no', width: 140, sorter: (a, b) => String(a.ref_no).localeCompare(String(b.ref_no)) },
    { title: 'Req Date', dataIndex: 'req_date', key: 'req_date', width: 120, sorter: (a, b) => dayjs(a.req_date || 0).unix() - dayjs(b.req_date || 0).unix(), render: (v) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 140, sorter: (a, b) => String(a.status || '').localeCompare(String(b.status || '')), render: (v: string) => <Tag color={
      v === 'pending' ? 'gold' : v === 'approved' ? 'blue' : v === 'partially_issued' ? 'purple' : v === 'issued' ? 'green' : v === 'rejected' ? 'red' : 'default'
    }>{v}</Tag> },
    { title: 'Requester', dataIndex: 'requester', key: 'requester', width: 120, sorter: (a, b) => String(a.requester || '').localeCompare(String(b.requester || '')) },
    { title: 'Requester Name', dataIndex: 'requester_name', key: 'requester_name', width: 180, sorter: (a, b) => String(a.requester_name || '').localeCompare(String(b.requester_name || '')) },
    { title: 'Item ID', dataIndex: 'item_id', key: 'item_id', width: 100, sorter: (a, b) => (a.item_id || 0) - (b.item_id || 0) },
    { title: 'Mat ID', dataIndex: 'mat_id', key: 'mat_id', width: 120, sorter: (a, b) => String(a.mat_id || '').localeCompare(String(b.mat_id || '')) },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true, sorter: (a, b) => String(a.description || '').localeCompare(String(b.description || '')) },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 80, sorter: (a, b) => String(a.unit || '').localeCompare(String(b.unit || '')) },
    { title: 'Ext Group', dataIndex: 'ext_group', key: 'ext_group', width: 140, sorter: (a, b) => String(a.ext_group || '').localeCompare(String(b.ext_group || '')) },
    { title: 'Requested WH', dataIndex: 'requested_wh_name', key: 'requested_wh_name', width: 180,
      sorter: (a, b) => String((a.requested_wh_name ?? whMap.get(Number(a.requested_wh_id)) ?? a.requested_wh_id ?? '')).localeCompare(String((b.requested_wh_name ?? whMap.get(Number(b.requested_wh_id)) ?? b.requested_wh_id ?? ''))),
      render: (_, r) => r.requested_wh_name ?? whMap.get(Number(r.requested_wh_id)) ?? r.requested_wh_id ?? '-',
      exportValue: (r: VwMatGapRequestItemOutstanding) => r.requested_wh_name ?? whMap.get(Number(r.requested_wh_id)) ?? r.requested_wh_id ?? '-' },
    { title: 'Requested', dataIndex: 'requested_qty', key: 'requested_qty', width: 120, align: 'right', sorter: (a, b) => (a.requested_qty || 0) - (b.requested_qty || 0) },
    { title: 'Issued', dataIndex: 'issued_qty', key: 'issued_qty', width: 120, align: 'right', sorter: (a, b) => (a.issued_qty || 0) - (b.issued_qty || 0) },
    { title: 'Outstanding', dataIndex: 'outstanding_qty', key: 'outstanding_qty', width: 130, align: 'right', sorter: (a, b) => (a.outstanding_qty || 0) - (b.outstanding_qty || 0) },
    { title: 'Item Notes', dataIndex: 'item_notes', key: 'item_notes', ellipsis: true, sorter: (a, b) => String(a.item_notes || '').localeCompare(String(b.item_notes || '')) },
  ], []);

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Issue Request Items</Title>
              <Text type="secondary">Itemized list of all request lines</Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Select showSearch allowClear placeholder="Ref No" value={refNo || undefined} onChange={(v) => setRefNo(v || '')} options={refNoOptions} style={{ width: 140 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())} />
            <Select showSearch allowClear placeholder="Requester (Code)" value={requester || undefined} onChange={(v) => setRequester(v || '')} options={requesterOptions} style={{ width: 140 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())} />
            <Select showSearch allowClear placeholder="Requester Name" value={requesterName || undefined} onChange={(v) => setRequesterName(v || '')} options={requesterNameOptions} style={{ width: 180 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())} />
            <Select placeholder="Status" allowClear value={status} onChange={(v) => setStatus(v)} style={{ width: 130 }} options={STATUS_OPTIONS.map(s => ({ value: s, label: s }))} />
            <Select showSearch allowClear placeholder="Mat ID" value={matId || undefined} onChange={(v) => setMatId(v || '')} options={matIdOptions} style={{ width: 130 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())} />
            <Select showSearch allowClear placeholder="Ext Group" value={extGroup || undefined} onChange={(v) => setExtGroup(v || '')} options={extGroupOptions} style={{ width: 140 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())} />
            <Select showSearch allowClear placeholder="Warehouse" value={warehouse || undefined} onChange={(v) => setWarehouse(v || '')} options={warehouseOptions} style={{ width: 280 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())} />
            <Input
              placeholder="Description contains..."
              allowClear
              value={descSearch}
              onChange={(e) => setDescSearch(e.target.value)}
              style={{ width: 220 }}
            />
            <RangePicker size="small" value={dateRange as any} onChange={(v) => setDateRange(v as any)} style={{ width: 220 }} />
            <Button size="small" icon={<ReloadOutlined />} onClick={() => refetch?.()}>Refresh</Button>
            <Button size="small" onClick={() => { setRefNo(''); setRequester(''); setRequesterName(''); setStatus(undefined); setDateRange(null); setMatId(''); setExtGroup(''); setWarehouse(''); }}>Reset</Button>
          </Space>
        </Col>
      </Row>

      <StandardTable<VwMatGapRequestItemOutstanding>
        columns={columns}
        dataSource={filtered}
        rowKey={(r) => `${r.ref_no}-${r.item_id}`}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
}
